import { EventEmitter } from 'events';
import { createHash } from 'crypto';
import axios from 'axios';

/**
 * @class TikTokDownloader
 * @extends EventEmitter
 * @description Advanced TikTok video downloader with multiple fallback methods and real-time events
 * @author lyssadev & chifft
 * @version 1.6.0
 */
export class TikTokDownloader extends EventEmitter {
    constructor(options = {}) {
        super();
        this.options = {
            timeout: options.timeout || 10000,
            maxRetries: options.maxRetries || 3,
            preferredQuality: options.preferredQuality || 'high',
            includeWatermark: options.includeWatermark || false,
            proxyUrl: options.proxyUrl || null,
            debug: options.debug || false,
            downloadPath: options.downloadPath || process.cwd(),
            autoRetry: options.autoRetry ?? true,
            cacheResults: options.cacheResults ?? true,
            maxCacheAge: options.maxCacheAge || 3600000, // 1 hour
            parallelDownloads: options.parallelDownloads || 3,
            outputFormat: options.outputFormat || 'mp4',
            includeAudio: options.includeAudio ?? true,
            customFileName: options.customFileName || null,
            headers: options.headers || {}
        };

        this.userAgents = [
            'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
            'Mozilla/5.0 (iPhone; CPU iPhone OS 17_1_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.1.2 Mobile/15E148 Safari/604.1',
            'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.2.1 Safari/605.1.15',
            'Mozilla/5.0 (Linux; Android 14; Pixel 7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.6099.43 Mobile Safari/537.36',
            'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
        ];

        // Enhanced API endpoints
        this.apis = {
            snaptik: 'https://api.snaptik.com/video-details',
            tikwm: 'https://www.tikwm.com/api',
            tikmate: 'https://api.tikmate.app/api',
            savett: 'https://savett.cc/api',
            tikdown: 'https://tikdown.org/api',
            tiktokapi: 'https://api16-normal-c-useast1a.tiktokv.com/aweme/v1/feed',
            dlpanda: 'https://dlpanda.com/api/v2/video',
            ssstik: 'https://ssstik.io/api/v1/download'
        };

        this.stats = {
            totalDownloads: 0,
            cacheHits: 0,
            cacheMisses: 0,
            errors: 0
        };
        this.cache = new Map();

        this.initializeEventHandlers();
    }

    /**
     * Initialize event handlers
     * @private
     */
    initializeEventHandlers() {
        this.on('error', (error) => {
            this.stats.errors++;
            this.debug(`Error occurred: ${error.message}`, 'error');
        });

        this.on('success', (info) => {
            const api = info.source;
            if (this.stats.apiUsage[api]) {
                this.stats.apiUsage[api].successes++;
                this.stats.apiUsage[api].averageResponseTime = 
                    (this.stats.apiUsage[api].averageResponseTime * (this.stats.apiUsage[api].successes - 1) + info.duration) 
                    / this.stats.apiUsage[api].successes;
            }
        });
    }

    /**
     * Clean expired cache entries
     * @private
     */
    cleanCache() {
        const now = Date.now();
        for (const [key, value] of this.cache.entries()) {
            if (now - value.timestamp > this.options.maxCacheAge) {
                this.cache.delete(key);
            }
        }
    }

    /**
     * Generate cache key for URL
     * @private
     * @param {string} url - Video URL
     * @returns {string} Cache key
     */
    generateCacheKey(url) {
        return createHash('md5').update(url).digest('hex');
    }

    /**
     * Get a random user agent
     * @private
     * @returns {string} Random user agent
     */
    getRandomUserAgent() {
        return this.userAgents[Math.floor(Math.random() * this.userAgents.length)];
    }

    /**
     * Log debug messages if debug mode is enabled
     * @private
     * @param {string} message - Debug message
     * @param {string} level - Log level (info, warn, error)
     */
    debug(message, level = 'info') {
        if (this.options.debug) {
            const timestamp = new Date().toISOString();
            this.emit('debug', { timestamp, level, message });
        }
    }

    /**
     * Extract video ID from TikTok URL
     * @private
     * @param {string} url - TikTok video URL
     * @returns {string|null} Video ID
     */
    async getVideoId(url) {
        try {
            // Support multiple URL formats
            const patterns = [
                /video\/(\d+)/,
                /\/v\/(\d+)/,
                /vm\.tiktok\.com\/(\w+)/,
                /vt\.tiktok\.com\/(\w+)/
            ];

            for (const pattern of patterns) {
                const match = url.match(pattern);
                if (match) return match[1];
            }

            // If no pattern matches, try to fetch the redirect URL
            if (url.includes('vm.tiktok.com') || url.includes('vt.tiktok.com')) {
                const response = await axios.head(url);
                const finalUrl = response.request.res.responseUrl;
                return this.getVideoId(finalUrl);
            }

            return null;
        } catch (error) {
            this.debug(`Error extracting video ID: ${error.message}`, 'error');
            return null;
        }
    }

    /**
     * Download TikTok video
     * @public
     * @param {string} url - TikTok video URL
     * @param {object} options - Download options
     * @returns {Promise<object>} Video information
     */
    async downloadVideo(url) {
        try {
            const videoId = await this.getVideoId(url);
            if (!videoId) {
                throw new Error('Could not extract video ID');
            }

            const cachedResult = this.cache.get(videoId);
            if (cachedResult) {
                this.stats.cacheHits++;
                return cachedResult;
            }

            this.stats.cacheMisses++;
            const response = await axios.get(url);
            const result = {
                id: videoId,
                url: url,
                data: response.data
            };

            this.cache.set(videoId, result);
            this.stats.totalDownloads++;
            return result;
        } catch (error) {
            this.stats.errors++;
            throw error;
        }
    }

    /**
     * Get download statistics
     * @public
     * @returns {object} Download statistics
     */
    getDetailedStats() {
        return { ...this.stats };
    }

    /**
     * Reset download statistics
     * @public
     */
    resetStats() {
        this.stats = {
            totalDownloads: 0,
            cacheHits: 0,
            cacheMisses: 0,
            errors: 0
        };
    }

    async downloadVideos(urls) {
        const downloads = urls.map(url => this.downloadVideo(url));
        const results = await Promise.all(downloads);
        return results;
    }
} 